import { ERROR_CODES } from '@mprolab/sdk'
import { reportClientError } from 'utils/reportClientError'

/**
 * Turns a caught transaction error into a sentence somebody can act on.
 *
 * `error.message` is read by nearly every branch below, and a provider that
 * throws a string or a bare object made the whole decoder throw — so the user
 * got no message at all, from the function whose job is producing one.
 */
export const getErrorCodesDescription = (t: (...props: any) => string, error: any) => {
  const message = String(error?.message ?? error?.reason ?? error ?? '')
  switch (true) {
    // Node Sales
    case message.includes(ERROR_CODES.LVL_UNITS_EXCEEDED_MSG):
      return t('globalErrors.exceededUnitsInLvl')
    case message.includes(ERROR_CODES.PROMO_UNITS_PER_WALLET_EXCEEDED_MSG):
      return t('globalErrors.exceededUnitsInPromoPerWallet')
    case message.includes(ERROR_CODES.MAX_UNITS_EXCEEDED_MSG):
    case message.includes(ERROR_CODES.MGA_MAX_UNITS_EXCEEDED_MSG):
      return t('globalErrors.maxUnitsExceeded')
    case message.includes(ERROR_CODES.PROMO_UNITS_EXCEEDED_MSG):
      return t('globalErrors.exceededUnitsInPromo')
    case message.includes(ERROR_CODES.PROMO_WALLET_NOT_ELIGIBLE_MSG):
      return t('globalErrors.promoWalletNotEligible')
    case message.includes(ERROR_CODES.MGA_PROMO_WALLET_NOT_ELIGIBLE_MSG):
      return t('globalErrors.promoExceededMax')
    case message.includes(ERROR_CODES.NOT_WHITELISTED):
      return t('globalErrors.notWhitelisted')
    case message.includes(ERROR_CODES.NOTHING_TO_CLAIM):
    case message.includes(ERROR_CODES.NO_TOKENS_TO_CLAIM):
      return t('globalErrors.noTokensToClaim')
    // Stakes — each of these has an answer, and none of them is "try again".
    case message.includes(ERROR_CODES.UNSTAKE_NOT_STARTED):
      return t('globalErrors.unstakeNotStarted')
    case message.includes(ERROR_CODES.STAKE_PERIOD_ENDED):
      return t('globalErrors.stakePeriodEnded')
    case message.includes(ERROR_CODES.COMPOUND_DISABLED):
      return t('globalErrors.compoundDisabled')
    case message.includes(ERROR_CODES.NO_REWARDS_TO_COMPOUND):
      return t('globalErrors.noRewardsToCompound')
    case message.includes(ERROR_CODES.CONTRACT_PAUSED):
      return t('globalErrors.contractPaused')
    // The wallet, not the contract.
    case message.includes(ERROR_CODES.WALLETCONNECT_NO_CHAIN_SWITCH):
      return t('globalErrors.switchNetworkYourself')
    case message.includes(ERROR_CODES.INSUFFICIENT_FUNDS_FOR_GAS):
      return t('globalErrors.noGas')
    case message.includes(ERROR_CODES.NO_SIGNER):
      return t('globalErrors.walletNotReady')
    case message.includes(ERROR_CODES.NETWORK_UNREACHABLE_WEBKIT):
    case message.includes(ERROR_CODES.NETWORK_UNREACHABLE):
      return t('globalErrors.networkUnreachable')
    case message.includes(ERROR_CODES.WALLET_CONNECT_FAILED):
      return t('globalErrors.walletConnectFailed')
    case message.includes(ERROR_CODES.WALLET_SESSION_EXPIRED):
      return t('globalErrors.walletSessionExpired')
    // Transaction Rejected
    case error?.code === ERROR_CODES.SIGN_ERROR:
    case error?.code === ERROR_CODES.ACTION_REJECTED:
    case message.includes(ERROR_CODES.MPRO_WALLET_ACTION_REJECTED_MSG):
      return t('globalErrors.rejected')
    // Insufficient Funds
    case error?.code === ERROR_CODES.INSUFFICIENT_FUNDS_ERROR_MSG:
    case error?.code === ERROR_CODES.INSUFFICIENT_FUNDS_ERROR_CODE:
    case error?.error?.code === ERROR_CODES.INSUFFICIENT_FUNDS_ERROR_CODE:
      return t('globalErrors.insufficientFunds')
    default:
      /*
       * Nothing here matched, so the user is being told "something went wrong"
       * — the one answer that helps nobody. Recorded unhandled with the raw
       * text, because the count of these IS the queue of sentences still to
       * write, and without the raw text the next reader has nothing to name.
       * Six of these on the stake claim on 6 September had no text anywhere.
       */
      reportClientError({
        message: `unnamed: ${message.slice(0, 300) || 'no message on the error'}`,
        kind: 'transaction',
        handled: false
      })
      return t('globalErrors.somethingWentWrong')
  }
}
